<template>
    <div class="main-content video-edit">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 class="mb-1">{{ isCreating ? "New Video" : "Edit Video" }}</h2>
            <router-link class="btn btn-sm btn-secondary self-start sm:self-center" :to="{ name: 'video-portal' }">
                <span aria-hidden="true">←</span> Back to Portal
            </router-link>
        </div>
        <p class="diagonal-lines mb-4"></p>

        <div v-if="saveSuccessMessage" class="alert alert-success py-2" role="status">{{ saveSuccessMessage }}</div>
        <div v-if="saveError" class="alert alert-danger py-2" role="alert">{{ saveError }}</div>
        <div v-if="deleteError" class="alert alert-danger py-2" role="alert">{{ deleteError }}</div>

        <div v-if="loading" class="flex justify-center items-center py-12">
            <vue-loaders-ball-beat scale="2" color="black" />
        </div>
        <div v-else-if="error" class="alert alert-danger" role="alert">
            <strong>Video could not be loaded.</strong>
            <span class="block mt-1">Return to the portal and try again.</span>
        </div>

        <form v-else-if="form" class="edit-workspace flex flex-col lg:flex-row lg:items-start" @submit.prevent="saveVideo">
            <div class="edit-main flex-1 min-w-0">
                <div class="title-editor p-3 md:p-4">
                    <admin-form-field
                        id="video-title"
                        v-model="form.title"
                        label="Video title"
                        variant="title"
                        :compact="false"
                        hide-label
                        placeholder="Video title"
                    />
                </div>

                <section class="edit-section p-3 md:p-4">
                    <div v-if="form.src" class="video-source-preview mb-4">
                        <span class="field-label">Video preview</span>
                        <div
                            v-if="isEmbedded"
                            class="video-embed-preview w-full aspect-video bg-black"
                            v-html="form.src"
                        ></div>
                        <video
                            v-else
                            :key="form.src"
                            :src="form.src"
                            :poster="form.thumb"
                            class="video-preview w-full"
                            controls
                            preload="metadata"
                        ></video>
                    </div>

                    <admin-toggle-field
                        id="video-is-embed"
                        v-model="form.is_embed"
                        label="Embedded video"
                        active-label="Embed"
                        inactive-label="File"
                        :true-value="true"
                        :false-value="false"
                        class="embed-toggle mb-4"
                    />

                    <admin-file-upload-field
                        v-if="!isEmbedded"
                        id="video-file"
                        v-model="form.src"
                        label="Video file"
                        upload-type="video"
                        :preview-url="form.src"
                        preview-label="Open current video"
                        help="No video file attached."
                        :max-upload-size-mb="250"
                    />
                    <admin-form-field
                        id="video-source"
                        v-model="form.src"
                        :type="isEmbedded ? 'textarea' : 'text'"
                        :rows="7"
                        :label="isEmbedded ? 'Embed code' : 'Or enter video source'"
                        :placeholder="isEmbedded ? '<iframe …></iframe>' : '/videos/example.mp4 or https://…'"
                        :help="isEmbedded
                            ? 'Paste the complete iframe embed code.'
                            : 'You can upload a file above or enter its public path or URL.'"
                        class="mt-4"
                        :compact="!isEmbedded"
                        :monospace="isEmbedded"
                    />
                </section>
            </div>

            <aside class="video-sidebar w-full lg:w-80 lg:flex-none">
                <div class="sidebar-heading px-3 py-2 font-semibold">Video settings</div>
                <section class="sidebar-section flex flex-col gap-3 p-3">
                    <div class="sidebar-actions flex items-center gap-2" :class="isCreating ? 'justify-end' : 'justify-between'">
                        <button
                            v-if="!isCreating"
                            type="button"
                            class="btn btn-outline-danger btn-sm"
                            :disabled="saving || deleting"
                            @click="deleteDialogOpen = true"
                        >
                            Delete
                        </button>
                        <button type="submit" class="btn btn-danger btn-sm sidebar-save-button" :disabled="saving || deleting">
                            {{ submitButtonLabel }}
                        </button>
                    </div>
                </section>

                <div class="sidebar-heading px-3 py-2 font-semibold">Thumbnail</div>
                <section class="sidebar-section p-3">
                    <button
                        v-if="isEmbedded"
                        type="button"
                        class="btn btn-sm btn-secondary w-full mb-3"
                        :disabled="!form.src || fetchingThumbnail || saving || deleting"
                        @click="fetchEmbedThumbnail"
                    >
                        {{ fetchingThumbnail ? "Getting thumbnail…" : "Get thumbnail from embed" }}
                    </button>
                    <small v-if="thumbnailError" class="feedback-message is-error block mb-3" role="alert">
                        {{ thumbnailError }}
                    </small>
                    <small v-else-if="thumbnailSuccess" class="feedback-message is-success block mb-3" role="status">
                        {{ thumbnailSuccess }}
                    </small>
                    <span class="field-label">Preview</span>
                    <div class="video-preview-frame flex justify-center items-center">
                        <img
                            v-if="form.thumb"
                            :src="form.thumb"
                            :alt="(form.title || 'Video') + ' thumbnail'"
                        />
                        <span v-else class="text-xs font-semibold text-pip-gray text-center">No thumbnail image</span>
                    </div>
                    <admin-file-upload-field
                        id="video-thumbnail"
                        v-model="form.thumb"
                        label="Thumbnail image"
                        upload-type="image"
                        :preview-url="form.thumb"
                        preview-label="View current image"
                        help="No thumbnail image attached."
                        class="mt-3"
                    />
                    <admin-form-field
                        id="video-thumbnail-source"
                        v-model="form.thumb"
                        label="Or enter thumbnail path"
                        placeholder="/images/video-thumbnail.jpg"
                        help="You can upload an image above or enter its public path or URL."
                        class="mt-3"
                    />
                </section>

            </aside>
        </form>

        <admin-confirm-dialog
            id="edit-delete-video"
            :open="deleteDialogOpen"
            title="Delete video?"
            :message="deleteConfirmationMessage"
            confirm-label="Delete video"
            working-label="Deleting…"
            :working="deleting"
            @confirm="deleteVideo"
            @cancel="deleteDialogOpen = false"
        />
    </div>
</template>

<script>
import {
    AdminConfirmDialog,
    AdminFileUploadField,
    AdminFormField,
    AdminToggleField,
} from "@c/shared/admin";

export default {
    name: "VideoEdit",
    components: { AdminConfirmDialog, AdminFileUploadField, AdminFormField, AdminToggleField },
    data() {
        return {
            form: null,
            loading: false,
            error: null,
            saving: false,
            saveError: null,
            saveSuccessMessage: null,
            deleteDialogOpen: false,
            deleting: false,
            deleteError: null,
            fetchingThumbnail: false,
            thumbnailError: null,
            thumbnailSuccess: null,
        };
    },
    computed: {
        isCreating() {
            return this.$route.name === "video-create";
        },
        submitButtonLabel() {
            if (this.saving) return this.isCreating ? "Creating…" : "Saving…";
            return this.isCreating ? "Create" : "Save";
        },
        isEmbedded() {
            return this.form && [1, "1", "y", true].includes(this.form.is_embed);
        },
        deleteConfirmationMessage() {
            if (!this.form) return "";
            return `“${this.form.title || "Untitled video"}” will be permanently removed. This cannot be undone.`;
        },
    },
    created() {
        if (this.isCreating) {
            this.form = this.emptyForm();
            return;
        }

        this.loading = true;
        axios
            .get(`/api/admin/videos/${this.$route.params.id}`)
            .then(response => { this.form = response.data; })
            .catch(error => { this.error = error; })
            .finally(() => { this.loading = false; });
    },
    methods: {
        emptyForm() {
            return { title: "", src: "", thumb: "", is_embed: false };
        },
        updatePayload() {
            return {
                title: this.form.title || null,
                src: this.form.src || null,
                thumb: this.form.thumb || null,
                is_embed: this.isEmbedded,
            };
        },
        async fetchEmbedThumbnail() {
            if (!this.isEmbedded || !this.form.src || this.fetchingThumbnail) return;

            this.fetchingThumbnail = true;
            this.thumbnailError = null;
            this.thumbnailSuccess = null;

            try {
                const response = await axios.post('/api/admin/media/embed-thumbnail', {
                    embed_code: this.form.src,
                });
                this.form.thumb = response.data.path;
                this.thumbnailSuccess = 'Thumbnail downloaded and attached. Save the video to keep this change.';
            } catch (error) {
                const response = error.response && error.response.data;
                const validationMessage = response && response.errors && response.errors.embed_code;
                this.thumbnailError = validationMessage
                    ? validationMessage[0]
                    : (response && response.message) || 'The thumbnail could not be downloaded. Please try again.';
            } finally {
                this.fetchingThumbnail = false;
            }
        },
        async saveVideo() {
            if (this.saving) return;
            this.saving = true;
            this.saveError = null;
            this.saveSuccessMessage = null;

            try {
                const creating = this.isCreating;
                const response = creating
                    ? await axios.post("/api/admin/videos", this.updatePayload())
                    : await axios.put(`/api/admin/videos/${this.$route.params.id}`, this.updatePayload());
                this.form = response.data;
                this.saveSuccessMessage = creating ? "Video created successfully." : "Video updated successfully.";

                if (creating) {
                    await this.$router.replace({ name: "video-edit", params: { id: response.data.id } });
                }
            } catch (error) {
                const errors = error.response && error.response.data && error.response.data.errors;
                const firstField = errors && Object.keys(errors)[0];
                this.saveError = firstField
                    ? errors[firstField][0]
                    : `Video could not be ${this.isCreating ? "created" : "saved"}.`;
            } finally {
                this.saving = false;
            }
        },
        async deleteVideo() {
            if (this.isCreating || this.deleting) return;
            this.deleting = true;
            this.deleteError = null;

            try {
                await axios.delete(`/api/admin/videos/${this.$route.params.id}`);
                this.deleteDialogOpen = false;
                await this.$router.push({ name: "video-portal", query: { deleted: "1" } });
            } catch (error) {
                this.deleteDialogOpen = false;
                this.deleteError = "Video could not be deleted. Please try again.";
            } finally {
                this.deleting = false;
            }
        },
    },
};
</script>

<style scoped lang="scss">
.edit-workspace { border: 1px solid var(--color-light-grey); background: #fff; }
.video-sidebar { background: #f8f8f8; border-top: 1px solid var(--color-light-grey); }

.sidebar-heading {
    border-bottom: 1px solid var(--color-light-grey);
    background: var(--color-gallery);
    color: var(--color-mine-shaft);
    font-size: var(--font-size-sm);
}

.sidebar-section { border-bottom: 1px solid var(--color-light-grey); }
.sidebar-section:last-child { border-bottom: 0; }
.sidebar-save-button { border-radius: 4px !important; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12); }

.field-label {
    display: block;
    margin-bottom: 0.35rem;
    color: var(--color-pip-gray);
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-bold);
    letter-spacing: var(--tracking-wider);
    text-transform: uppercase;
}

.video-preview-frame {
    width: 100%;
    aspect-ratio: 16 / 9;
    border: 1px solid var(--color-light-grey);
    background: #111;
    overflow: hidden;
}

.video-preview-frame img { width: 100%; height: 100%; object-fit: cover; }
.video-preview { display: block; background: #111; }
.video-source-preview { max-width: 56rem; }
.video-embed-preview { position: relative; overflow: hidden; }
.video-embed-preview ::v-deep iframe { width: 100%; height: 100%; border: 0; }
.embed-toggle { max-width: 24rem; border: 1px solid var(--color-light-grey); }
.feedback-message { font-size: var(--font-size-xs); font-weight: var(--font-weight-semibold); }
.feedback-message.is-error { color: #a51600; }
.feedback-message.is-success { color: #23783a; }

@media (min-width: 1024px) {
    .video-sidebar { border-top: 0; border-left: 1px solid var(--color-light-grey); }
}
</style>
